import React, { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import "../../Styles/Feefrequency.css";
import "../../Styles/yellocalfcss/ReceiptLayout.css";

// Resizes an image file to fit within a 240x80 box before upload,
// keeping the aspect ratio. Images already smaller are left as-is.
function resizeImage(file) {
  const MAX_W = 240;
  const MAX_H = 80;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(MAX_W / img.width, MAX_H / img.height, 1);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Resize failed."))),
          "image/png",
        );
      };
      img.onerror = () => reject(new Error("Could not load image."));
      img.src = reader.result;
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Uploads images picked via the CKEditor image button to the backend
// (resized to 30x30) and embeds the returned URL into the content.
function CKEditorUploadAdapter(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => ({
    upload() {
      return loader.file
        .then((file) => resizeImage(file))
        .then(
          (blob) =>
            new Promise((resolve, reject) => {
              const data = new FormData();
              data.append("upload", blob, "image.png");
              fetch(ApiConfig.ckeditorupload, {
                method: "POST",
                headers: {
                  customer_id: localStorage.getItem("customer_id"),
                  api_key: localStorage.getItem("api_key"),
                },
                body: data,
              })
                .then((res) => res.json())
                .then((json) => {
                  if (json && json.url) {
                    resolve({ default: json.url });
                  } else {
                    reject(json?.error?.message || "Image upload failed.");
                  }
                })
                .catch(() => reject("Image upload failed."));
            }),
        );
    },
    abort() {},
  });
}

const editorConfig = { extraPlugins: [CKEditorUploadAdapter] };

export default function ReceiptLayoutSettings() {
  const [header, setHeader] = useState("");
  const [footer, setFooter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfPath, setPdfPath] = useState(""); // State to store the PDF URL
  const [isConfirmed, setIsConfirmed] = useState(false);

  // 1. Fetch data on load
  useEffect(() => {
    fetchInvoiceLayout();
  }, []);

  const fetchInvoiceLayout = async () => {
    setIsLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");

    const requestBody = {
      customer_id: Number(customer_id),
      api_key: api_key,
      language: "en",
    };

    try {
      const response = await simplePostCall(
        ApiConfig.parentintegrationsettingsget,
        JSON.stringify(requestBody),
      );
      if (response?.result && response?.invoice_layout?.length > 0) {
        setHeader(response.invoice_layout[0].invoice_header || "");
        setFooter(response.invoice_layout[0].invoice_footer || "");
      }
    } catch (error) {
      console.error("Error loading layout:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmFinalize = () => {
    setIsConfirmed(true);
    toast.success("PDF confirmed and finalized!");
  };
  // 2. Handle Save and Get PDF Path
  const handleSaveAndPreview = async () => {
    setIsLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");
    const user_id = localStorage.getItem("user_id");

    const requestBody = {
      customer_id: Number(customer_id),
      api_key: api_key,
      user_id: Number(user_id),
      language: "en",
      invoice_layout: [{ invoice_header: header, invoice_footer: footer }],
    };

    try {
      // Calling the API you mentioned
      const response = await simplePostCall(
        ApiConfig.invoicelayout,
        JSON.stringify(requestBody),
      );

      if (response?.result && response?.path) {
        setShowPreview(false);
        setPdfPath(""); // Set the URL: http://localhost:3000/.../previewTest.pdf
        toast.success("Layout saved! Generating PDF preview...", {
          autoClose: 1500,
        });

        setTimeout(() => {
          setPdfPath(response.path);
          setShowPreview(true);
        }, 1500);
      } else if (response?.result) {
        toast.success("Saved successfully!");
      } else {
        toast.warning("Save completed, but no preview available.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong while saving layout!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="receiplayout-container">
      <ToastContainer />
      <div className="special-header panel-white-all">
        <h4 className="page-title">Invoice Layout & PDF Review</h4>
      </div>

      <div className="yc-list-body">
        <div className="rl-grid">
          {/* LEFT SIDE: Editors */}
          <div className="rl-editor-card">
            <div className="editor-section">
              <label>Header Content</label>
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={header}
                onChange={(event, editor) => setHeader(editor.getData())}
              />
            </div>

            <div className="editor-section">
              <label>Footer Content</label>
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={footer}
                onChange={(event, editor) => setFooter(editor.getData())}
              />
            </div>

            <button
              className="btn btn-primary rl-save-btn"
              onClick={handleSaveAndPreview}
              disabled={isLoading}
            >
              {isLoading ? "Generating PDF..." : "Save & Generate PDF Preview"}
            </button>
          </div>

          {/* RIGHT SIDE: PDF Review */}
          <div className="rl-preview-card">
            <h5 className="rl-preview-title">PDF Review</h5>

              {pdfPath ? (
                <div
                  style={{
                    height: "418px",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background: "#fff",
                    overflow: "scroll",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      overflowY: "auto",
                    }}
                  >
                    {showPreview && pdfPath && (
                      <iframe
                        src={`${pdfPath}#toolbar=0&navpanes=0`}
                        width="100%"
                        height="100%"
                        title="Invoice PDF Preview"
                        style={{ border: "none" }}
                      ></iframe>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      padding: "10px",
                      borderTop: "1px solid #ddd",
                      background: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    <a
                      href={pdfPath}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-primary btn-sm"
                    >
                      Open in New Tab
                    </a>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleConfirmFinalize}
                      disabled={isConfirmed}
                    >
                      {isConfirmed ? "Confirmed" : "Confirm & Finalize"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                  <i
                    className="bi bi-file-pdf"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <p>Save changes to generate and review PDF</p>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
